import SearchIcon from '@mui/icons-material/Search';
import UserPlusIcon from '@mui/icons-material/PersonAddAlt';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiError,
  InviteMemberDto,
  TeamMember,
  UpdateMemberRoleDto,
  WorkspaceRoleString,
} from 'api';
import { teamService, WorkspaceRoleLabels } from 'api';
import { useAuthStore } from 'features/auth/stores/authStore';
import { ChevronDown, MoreHorizontal, Plus } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, NonScrollingTableBox } from 'shared/components/DataTable';
import { HeroContainer } from 'shared/components/HeroContainer';
import {
  HeroDecorCircleBottom,
  HeroDecorCircleTop,
  HeroStatCard,
  HeroStatDot,
  HeroStatHelperText,
  HeroStatLabel,
  HeroStatsGrid,
  HeroStatValue,
  HeroStatValueRow,
  HeroSubtitle,
} from 'shared/components/HeroContainer';
import { queryKeys } from 'shared/constants/queryKeys';
import { useDebounce } from 'shared/hooks/useDebounce';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ActionCell,
  AddRateButton,
  ApplyButton,
  BillableCell,
  ClearFiltersLink,
  DeleteMenuItem,
  EmailText,
  FilterSelect,
  FilterToolbarCard,
  HeroContentRow,
  HeroTitle,
  HeroTitleBlock,
  InviteMemberButton,
  MemberAvatar,
  MemberCellBox,
  MemberNameText,
  PageRoot,
  RateValue,
  RolePill,
  RoleCellButton,
  RowMenuButton,
  rowMenuSlotProps,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  TableFooterRow,
  TeamTableCard,
} from '../team.styles';

import { EditRoleModal } from './EditRoleModal';
import { InviteMemberModal } from './InviteMemberModal';
import { SetBillableRateModal } from './SetBillableRateModal';

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface RoleModalState {
  open: boolean;
  userId: string | null;
}

interface BillableRateModalState {
  open: boolean;
  userId: string | null;
}

type StatusFilter = 'Active' | 'Inactive' | 'all';
type RoleFilter = 'all' | WorkspaceRoleString;

interface RowMenuState {
  anchorEl: HTMLElement | null;
  userId: string | null;
}

const ROLE_FILTER_OPTIONS: RoleFilter[] = ['Admin', 'ProjectManager', 'TeamMember'];

const TeamPage: React.FC = () => {
  const { canInviteMember, canEditMemberRole, canSetBillableRate, canDeactivateMember, isOwner } =
    usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingStatus, setPendingStatus] = useState<StatusFilter>('Active');
  const [pendingRole, setPendingRole] = useState<RoleFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('Active');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [rowMenu, setRowMenu] = useState<RowMenuState>({ anchorEl: null, userId: null });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [roleModal, setRoleModal] = useState<RoleModalState>({ open: false, userId: null });
  const [rateModal, setRateModal] = useState<BillableRateModalState>({ open: false, userId: null });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'name', direction: 'asc' });

  const debouncedSearch = useDebounce(searchTerm, 300);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: members = [], isLoading } = useQuery({
    queryKey: queryKeys.team.members(),
    queryFn: () => teamService.getMembers().then((res) => res.data.data),
  });

  const inviteMutation = useMutation({
    mutationFn: (data: InviteMemberDto) => teamService.inviteMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all });
      setInviteOpen(false);
      enqueueSnackbar('Invitation sent', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const setBillableRateMutation = useMutation({
    mutationFn: ({ userId, rate }: { userId: string; rate: number | undefined }) =>
      teamService.setBillableRate(userId, { billableRate: rate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all });
      setRateModal({ open: false, userId: null });
      enqueueSnackbar('Billable rate updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update billable rate', { variant: 'error' });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateMemberRoleDto }) =>
      teamService.updateMemberRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all });
      setRoleModal({ open: false, userId: null });
      enqueueSnackbar('Role updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update role', { variant: 'error' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => teamService.deactivateMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all });
      enqueueSnackbar('Member deactivated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to deactivate member', { variant: 'error' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (userId: string) => teamService.reactivateMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all });
      enqueueSnackbar('Member reactivated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to reactivate member', { variant: 'error' });
    },
  });

  const activeCount = useMemo(() => members.filter((m) => m.status === 'Active').length, [members]);
  const inactiveCount = useMemo(
    () => members.filter((m) => m.status === 'Inactive').length,
    [members],
  );

  const handleSort = (columnId: string) => {
    if (sortConfig.column === columnId) {
      setSortConfig({
        column: columnId,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortConfig({ column: columnId, direction: 'desc' });
    }
  };

  const filteredMembers = useMemo(() => {
    const filtered = members.filter((m) => {
      const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const matchesSearch =
        !debouncedSearch ||
        m.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        m.email.toLowerCase().includes(debouncedSearch.toLowerCase());
      return matchesStatus && matchesRole && matchesSearch;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortConfig.column === 'name') {
        comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      } else if (sortConfig.column === 'email') {
        comparison = a.email.toLowerCase().localeCompare(b.email.toLowerCase());
      }
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [members, statusFilter, roleFilter, debouncedSearch, sortConfig]);

  const hasFilters = statusFilter !== 'Active' || roleFilter !== 'all' || Boolean(searchTerm);

  const handleApply = () => {
    setSearchTerm(pendingSearch);
    setStatusFilter(pendingStatus);
    setRoleFilter(pendingRole);
  };

  const handleClearFilters = () => {
    setPendingSearch('');
    setPendingStatus('Active');
    setPendingRole('all');
    setSearchTerm('');
    setStatusFilter('Active');
    setRoleFilter('all');
  };

  const openRowMenu = (e: React.MouseEvent<HTMLElement>, userId: string) => {
    setRowMenu({ anchorEl: e.currentTarget, userId });
  };

  const closeRowMenu = () => {
    setRowMenu({ anchorEl: null, userId: null });
  };

  const currentMember = members.find((m) => m.userId === rowMenu.userId);

  const handleDeactivateToggle = () => {
    if (!rowMenu.userId) return;
    if (currentMember?.status === 'Active') {
      deactivateMutation.mutate(rowMenu.userId);
    } else {
      reactivateMutation.mutate(rowMenu.userId);
    }
    closeRowMenu();
  };

  const columns: DataTableColumn<TeamMember>[] = [
    {
      id: 'name',
      label: 'Member',
      width: '30%',
      sortable: true,
      sortValue: (m) => m.name.toLowerCase(),
      render: (m) => {
        const isInactive = m.status === 'Inactive';
        return (
          <MemberCellBox>
            <MemberAvatar src={m.avatarUrl} alt={m.name}>
              {m.name.charAt(0).toUpperCase()}
            </MemberAvatar>
            <MemberNameText inactive={isInactive}>{m.name || '—'}</MemberNameText>
          </MemberCellBox>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      width: '35%',
      sortable: true,
      sortValue: (m) => m.email.toLowerCase(),
      render: (m) => <EmailText>{m.email}</EmailText>,
    },
    {
      id: 'role',
      label: 'Role',
      width: 140,
      render: (m) => {
        const isReadOnly =
          !canEditMemberRole || m.userId === currentUserId || (m.role === 'Owner' && !isOwner);
        const pill = (
          <RolePill
            label={WorkspaceRoleLabels[m.role as WorkspaceRoleString] ?? m.role}
            roleKind={m.role}
            size="small"
          />
        );
        if (isReadOnly) return pill;
        return (
          <RoleCellButton onClick={() => setRoleModal({ open: true, userId: m.userId })}>
            {pill}
          </RoleCellButton>
        );
      },
    },
    {
      id: 'billableRate',
      label: 'Billable rate',
      width: 130,
      render: (m) => (
        <BillableCell>
          {m.billableRate != null && <RateValue>${m.billableRate.toFixed(2)}</RateValue>}
          {canSetBillableRate && (
            <AddRateButton
              variant="outlined"
              size="small"
              onClick={() => setRateModal({ open: true, userId: m.userId })}
            >
              {m.billableRate == null && <Plus size={12} />}
              {m.billableRate != null ? 'Edit' : 'Add rate'}
            </AddRateButton>
          )}
        </BillableCell>
      ),
    },
    {
      id: 'actions',
      label: '',
      width: canDeactivateMember ? 68 : 0,
      align: 'right',
      render: (m) => {
        const canActOnRow =
          canDeactivateMember && m.userId !== currentUserId && (m.role !== 'Owner' || isOwner);
        return (
          <ActionCell>
            {canActOnRow && (
              <RowMenuButton aria-label="More actions" onClick={(e) => openRowMenu(e, m.userId)}>
                <MoreHorizontal size={16} />
              </RowMenuButton>
            )}
          </ActionCell>
        );
      },
    },
  ];

  return (
    <PageRoot>
      <HeroContainer padding="24px 28px">
        <HeroDecorCircleTop aria-hidden />
        <HeroDecorCircleBottom aria-hidden />
        <HeroContentRow>
          <HeroTitleBlock>
            <HeroTitle>Team</HeroTitle>
            <HeroSubtitle>Manage members, roles, access, and billing rates.</HeroSubtitle>
            {canInviteMember && (
              <InviteMemberButton type="button" onClick={() => setInviteOpen(true)}>
                <UserPlusIcon sx={{ fontSize: 18 }} />
                Invite member
              </InviteMemberButton>
            )}
          </HeroTitleBlock>

          <HeroStatsGrid>
            <HeroStatCard>
              <HeroStatLabel>Active</HeroStatLabel>
              <HeroStatValueRow>
                <HeroStatDot dotcolor="#34D399" />
                <HeroStatValue>{activeCount}</HeroStatValue>
              </HeroStatValueRow>
              <HeroStatHelperText>Team members</HeroStatHelperText>
            </HeroStatCard>

            <HeroStatCard>
              <HeroStatLabel>All Members</HeroStatLabel>
              <HeroStatValueRow>
                <HeroStatDot dotcolor="rgba(255,255,255,0.6)" />
                <HeroStatValue>{members.length}</HeroStatValue>
              </HeroStatValueRow>
              <HeroStatHelperText>Total count</HeroStatHelperText>
            </HeroStatCard>

            <HeroStatCard>
              <HeroStatLabel>Inactive</HeroStatLabel>
              <HeroStatValueRow>
                <HeroStatDot dotcolor="rgba(255,255,255,0.3)" />
                <HeroStatValue>{inactiveCount}</HeroStatValue>
              </HeroStatValueRow>
              <HeroStatHelperText>Deactivated</HeroStatHelperText>
            </HeroStatCard>
          </HeroStatsGrid>
        </HeroContentRow>
      </HeroContainer>

      <FilterToolbarCard>
        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={{ fontSize: 16 }} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search by name or email"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
          />
        </SearchFieldBox>

        <FilterSelect
          value={pendingStatus}
          onChange={(e) => setPendingStatus(e.target.value as StatusFilter)}
          displayEmpty
          IconComponent={(props) => <ChevronDown {...props} size={16} strokeWidth={2} />}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All members</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </FilterSelect>

        <FilterSelect
          value={pendingRole}
          onChange={(e) => setPendingRole(e.target.value as RoleFilter)}
          displayEmpty
          IconComponent={(props) => <ChevronDown {...props} size={16} strokeWidth={2} />}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All roles</MenuItem>
          {ROLE_FILTER_OPTIONS.map((role) => (
            <MenuItem key={role} value={role}>
              {WorkspaceRoleLabels[role as WorkspaceRoleString]}
            </MenuItem>
          ))}
        </FilterSelect>

        {hasFilters && (
          <ClearFiltersLink type="button" onClick={handleClearFilters}>
            Clear filters
          </ClearFiltersLink>
        )}

        <ApplyButton variant="contained" disableElevation onClick={handleApply}>
          Apply
        </ApplyButton>
      </FilterToolbarCard>

      <TeamTableCard>
        <NonScrollingTableBox>
          <DataTable<TeamMember>
            columns={columns}
            rows={filteredMembers}
            getRowId={(m) => m.userId}
            loading={isLoading}
            emptyMessage="No members match your filters."
            sortColumn={sortConfig.column}
            sortDirection={sortConfig.direction}
            onSort={handleSort}
            headerFontSize="0.625rem"
            headerTextColor="#5A5578"
            headerHeight={36}
            headerPaddingY={0}
            rowHoverColor="rgba(110, 79, 247, 0.03)"
            rowHeight={58}
            cellPaddingX={20}
            cellPaddingY={0}
            fixedLayout
            containerSx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}
          />
        </NonScrollingTableBox>

        <TableFooterRow>
          <span>
            Showing {filteredMembers.length} of {members.length} members
          </span>
        </TableFooterRow>
      </TeamTableCard>

      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={rowMenuSlotProps}
      >
        {canDeactivateMember && (
          <DeleteMenuItem
            onClick={handleDeactivateToggle}
            disabled={deactivateMutation.isPending || reactivateMutation.isPending}
          >
            <ListItemText>
              {currentMember?.status === 'Active' ? 'Deactivate member' : 'Reactivate member'}
            </ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={(data) => inviteMutation.mutate(data)}
        isPending={inviteMutation.isPending}
        isViewerOwner={isOwner}
      />

      <EditRoleModal
        open={roleModal.open}
        memberName={members.find((m) => m.userId === roleModal.userId)?.name ?? ''}
        currentRole={
          (members.find((m) => m.userId === roleModal.userId)?.role ??
            'TeamMember') as WorkspaceRoleString
        }
        onClose={() => setRoleModal({ open: false, userId: null })}
        onSave={(role) =>
          roleModal.userId &&
          updateRoleMutation.mutate({ userId: roleModal.userId, data: { role } })
        }
        isPending={updateRoleMutation.isPending}
        isViewerOwner={isOwner}
      />

      <SetBillableRateModal
        open={rateModal.open}
        memberName={members.find((m) => m.userId === rateModal.userId)?.name ?? ''}
        currentRate={members.find((m) => m.userId === rateModal.userId)?.billableRate}
        onClose={() => setRateModal({ open: false, userId: null })}
        onSave={(rate) =>
          rateModal.userId && setBillableRateMutation.mutate({ userId: rateModal.userId, rate })
        }
        isPending={setBillableRateMutation.isPending}
      />
    </PageRoot>
  );
};

export default TeamPage;
